import {useMemo} from 'react'
import {useSelector} from 'react-redux'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {getIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {CapabilityContext} from '@vio/core/src/brand/capabilities/types/core'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {getLanguageCode} from '@vio/core/src/localization/services/locale'

/**
 * Assembles the runtime context passed to capability resolver functions.
 * Single place where context is gathered — resolvers never reach into
 * React context directly.
 *
 * Produces the value mounted on `<CapabilityRuntimeContext.Provider>` (in
 * `@vio/core/src/_web/brand/hooks/CapabilityRuntimeContext`) at the top of
 * each app shell. `useBrandCapabilities` in `core` then reads that
 * context. The producer stays in `shared/whiteLabel/` because it depends
 * on atlas (`useDeviceLayout`); moving it to `core` would create a
 * `core → atlas` circular package dependency, since atlas already
 * depends on core.
 */
export const useCapabilityContext = (): CapabilityContext => {
  const {isMobile} = useDeviceLayout()
  // Some stores never set `meta.languageCode`; an empty locale keeps locale
  // resolvers from throwing.
  const locale = useSelector(getLanguageCode) ?? ''
  const isAuthenticated = useSelector(getIsAuthenticated)
  const toggle = useToggle()

  return useMemo(
    () => ({
      isMobile,
      locale,
      isAuthenticated,
      experimentVariant: (toggleId: string) => {
        // Pass identity variant args so Opticks returns the active variant
        // name. Calling `toggle(id)` with no variants returns `undefined`
        // for any active variant. Two args cover the 2-variant experiments
        // we use today; widen if a 3+ variant capability is added.
        //
        // `useBrandCapabilities` can be called before `InitializeABTesting`
        // has had a chance to run `Opticks.setUserId`, in which case
        // Opticks throws "Fatal error: user id is not set". Treat that as
        // "no decision yet" — resolvers fall back to the inactive variant
        // and re-run once the user id lands.
        try {
          const value = toggle(toggleId, 'a', 'b')
          return typeof value === 'string' ? value : undefined
        } catch {
          return undefined
        }
      }
    }),
    [isMobile, locale, isAuthenticated, toggle]
  )
}
